/**
 * markdownSanitizer.js
 *
 * Scans AI-generated markdown for common problems and auto-fixes them
 * before the content reaches ReactMarkdown. This prevents garbled output
 * caused by:
 *   - Doubled heading markers (## ## Title → ## Title)
 *   - Consecutive duplicate lines / headings
 *   - Oversized headings (H1 used everywhere → promoted to H2/H3)
 *   - Excessive blank lines
 *   - Bold-only lines that should be headings
 *   - Raw "---" ruler spam
 *   - Trailing whitespace on heading lines
 *   - Mixed heading hierarchy (skipping levels)
 *   - Table cells with list marker patterns (- [SSX] → \- [SSX])
 *   - Empty table cells that confuse markdown parsers (- → –)
 */

/* ─────────────────────────────────────────────
   ISSUE DETECTORS
   Each returns an array of { line, issue, suggestion } objects.
   ───────────────────────────────────────────── */

/**
 * Find lines with doubled heading markers, e.g. "## ## Title" or "# # Title"
 */
function detectDoubledHeadings(lines) {
  const issues = [];
  lines.forEach((line, i) => {
    // Matches: one or more # groups separated by spaces, e.g. ## ## or # ## ###
    if (/^(#{1,6}\s+)+#{1,6}\s/.test(line) || /^#{1,6}\s+#{1,6}/.test(line)) {
      issues.push({ lineIndex: i, type: 'doubled_heading', original: line });
    }
  });
  return issues;
}

/**
 * Find consecutive identical or near-identical lines (duplicate content).
 */
function detectDuplicateLines(lines) {
  const issues = [];
  for (let i = 1; i < lines.length; i++) {
    const curr = lines[i].trim();
    const prev = lines[i - 1].trim();
    if (curr && prev && curr === prev) {
      issues.push({ lineIndex: i, type: 'duplicate_line', original: lines[i] });
    }
  }
  return issues;
}

/**
 * Find runs of 3+ consecutive blank lines.
 */
function detectExcessiveBlankLines(lines) {
  const issues = [];
  let count = 0;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].trim() === '') {
      count++;
      if (count >= 3) {
        issues.push({ lineIndex: i, type: 'excess_blank', original: lines[i] });
      }
    } else {
      count = 0;
    }
  }
  return issues;
}

/**
 * Find H1 headings that appear more than once
 * (AI agents often repeat the section name as H1 at the top of every chunk).
 */
function detectRepeatedH1s(lines) {
  const issues = [];
  const h1s = lines
    .map((l, i) => ({ line: l, i }))
    .filter(({ line }) => /^#\s+\S/.test(line));

  if (h1s.length > 1) {
    // Flag all H1s after the first
    h1s.slice(1).forEach(({ i, line }) => {
      issues.push({ lineIndex: i, type: 'repeated_h1', original: line });
    });
  }
  return issues;
}

/**
 * Find lines that are just bold text used as a pseudo-heading,
 * e.g. "**Company Overview**" on its own line.
 */
function detectBoldAsHeading(lines) {
  const issues = [];
  lines.forEach((line, i) => {
    if (/^\*\*[^*]+\*\*\s*$/.test(line.trim()) || /^__[^_]+__\s*$/.test(line.trim())) {
      // Only flag if it's a standalone line (prev & next are blank or heading)
      const prevBlank = i === 0 || lines[i - 1].trim() === '';
      const nextBlank = i === lines.length - 1 || lines[i + 1].trim() === '';
      if (prevBlank || nextBlank) {
        issues.push({ lineIndex: i, type: 'bold_as_heading', original: line });
      }
    }
  });
  return issues;
}

/**
 * Find heading lines that skip levels (e.g. H1 then H4, skipping H2/H3).
 */
function detectSkippedHeadingLevels(lines) {
  const issues = [];
  let prevLevel = 0;
  lines.forEach((line, i) => {
    const m = line.match(/^(#{1,6})\s/);
    if (m) {
      const level = m[1].length;
      if (prevLevel > 0 && level > prevLevel + 1) {
        issues.push({ lineIndex: i, type: 'skipped_heading', original: line, prevLevel, level });
      }
      prevLevel = level;
    }
  });
  return issues;
}

/**
 * Find horizontal rules that appear 3+ times consecutively or are mis-formatted.
 */
function detectRulerSpam(lines) {
  const issues = [];
  let count = 0;
  for (let i = 0; i < lines.length; i++) {
    if (/^[-*_]{3,}\s*$/.test(lines[i].trim())) {
      count++;
      if (count > 1) {
        issues.push({ lineIndex: i, type: 'ruler_spam', original: lines[i] });
      }
    } else if (lines[i].trim() !== '') {
      count = 0;
    }
  }
  return issues;
}

/**
 * Detect and fix malformed table structures
 * - Inconsistent column counts
 * - Cells with content that looks like list markers (e.g., "- [SSX]")
 * - Missing separator rows
 */
function detectMalformedTables(lines) {
  const issues = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Check if this looks like a table row (starts with |)
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      // Check if it's a separator row
      const isSeparator = /^\s*\|[\s\-:|]+\|\s*$/.test(line);
      if (!isSeparator) {
        // Check if this row has cells that start with "- " which can break parsing
        if (/\|\s*-\s+\[/.test(line)) {
          issues.push({ lineIndex: i, type: 'table_list_confusion', original: line });
        }
      }
    }
  }
  return issues;
}

/* ─────────────────────────────────────────────
   FIXERS
   ───────────────────────────────────────────── */

/**
 * Fix doubled heading markers: "## ## Title" → "## Title"
 */
function fixDoubledHeadings(line) {
  // Collapse repeated heading markers into the first one
  return line.replace(/^(#{1,6})(\s+#+\s+|\s+#{1,6}\s+)/, (match, first) => first + ' ');
}

/**
 * Promote all H1s after the first to H2.
 */
function demoteExtraH1s(lines) {
  let firstH1Found = false;
  return lines.map(line => {
    if (/^#\s+\S/.test(line)) {
      if (!firstH1Found) {
        firstH1Found = true;
        return line;
      }
      // Demote to H2
      return line.replace(/^#\s+/, '## ');
    }
    return line;
  });
}

/**
 * Convert standalone bold lines to H4 headings.
 */
function convertBoldLinesToHeadings(lines) {
  return lines.map((line, i) => {
    if (/^\*\*[^*]+\*\*\s*$/.test(line.trim()) || /^__[^_]+__\s*$/.test(line.trim())) {
      const prevBlank = i === 0 || lines[i - 1].trim() === '';
      const nextBlank = i === lines.length - 1 || lines[i + 1].trim() === '';
      if (prevBlank || nextBlank) {
        // Strip bold markers and convert to heading
        const text = line.trim().replace(/^\*\*|\*\*$/g, '').replace(/^__|__$/g, '').trim();
        return `#### ${text}`;
      }
    }
    return line;
  });
}

/**
 * Collapse 3+ consecutive blank lines to exactly 2.
 */
function collapseExcessiveBlanks(lines) {
  const result = [];
  let blankCount = 0;
  for (const line of lines) {
    if (line.trim() === '') {
      blankCount++;
      if (blankCount <= 2) result.push(line);
    } else {
      blankCount = 0;
      result.push(line);
    }
  }
  return result;
}

/**
 * Remove consecutive duplicate lines.
 */
function removeDuplicateLines(lines) {
  return lines.filter((line, i) => {
    if (i === 0) return true;
    return line.trim() !== lines[i - 1].trim() || line.trim() === '';
  });
}

/**
 * Remove consecutive duplicate horizontal rules.
 */
function removeRulerSpam(lines) {
  return lines.filter((line, i) => {
    if (!/^[-*_]{3,}\s*$/.test(line.trim())) return true;
    // Check if previous non-blank line was also a ruler
    for (let j = i - 1; j >= 0; j--) {
      if (lines[j].trim() !== '') {
        return !/^[-*_]{3,}\s*$/.test(lines[j].trim());
      }
    }
    return true;
  });
}

/**
 * Fix heading hierarchy: if levels are skipped (H1 → H4), bridge the gap.
 */
function fixSkippedHeadingLevels(lines) {
  let prevLevel = 0;
  return lines.map(line => {
    const m = line.match(/^(#{1,6})\s+(.*)/);
    if (!m) return line;
    const level = m[1].length;
    const text = m[2];
    if (prevLevel > 0 && level > prevLevel + 1) {
      // Cap the jump to prevLevel + 1
      const fixedLevel = prevLevel + 1;
      prevLevel = fixedLevel;
      return `${'#'.repeat(fixedLevel)} ${text}`;
    }
    prevLevel = level;
    return line;
  });
}

/**
 * Trim trailing whitespace from heading lines.
 */
function trimHeadingWhitespace(lines) {
  return lines.map(line => {
    if (/^#{1,6}\s/.test(line)) return line.trimEnd();
    return line;
  });
}

/**
 * Fix table cells that contain content that looks like list markers.
 * Also handles other common table parsing issues.
 */
function fixTableCellContent(lines) {
  const result = [];
  let inTable = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();
    
    // Check if this looks like a table row
    const isTableRow = trimmedLine.startsWith('|') && trimmedLine.endsWith('|');
    const isSeparator = /^\s*\|[\s\-:|]+\|\s*$/.test(trimmedLine);
    
    if (isTableRow) {
      // Entering or continuing a table
      if (!inTable && i > 0 && lines[i - 1].trim() !== '') {
        // Add blank line before table if missing
        result.push('');
      }
      inTable = true;
      
      // Skip separator rows (don't modify them)
      if (isSeparator) {
        result.push(line);
        continue;
      }
      
      // Process table data row
      const parts = line.split('|');
      const fixed = parts.map((cell, idx) => {
        // Skip first and last (empty due to leading/trailing pipes)
        if (idx === 0 || idx === parts.length - 1) return cell;
        
        const trimmed = cell.trim();
        const leadingSpace = cell.match(/^\s*/)[0];
        const trailingSpace = cell.match(/\s*$/)[0];
        
        // Case 1: Cell starts with "- " followed by content (e.g., "- [SSX]")
        // Insert zero-width space after dash to prevent list marker interpretation
        if (/^-\s/.test(trimmed)) {
          const fixed = trimmed.replace(/^-(\s)/, '-\u200B$1');
          return leadingSpace + fixed + trailingSpace;
        }
        
        // Case 2: Cell is just a single dash "-"
        // Replace with non-breaking hyphen to avoid any parser confusion
        if (trimmed === '-') {
          return leadingSpace + '‑' + trailingSpace; // non-breaking hyphen (U+2011)
        }
        
        return cell;
      });
      result.push(fixed.join('|'));
    } else {
      // Not a table row
      if (inTable && trimmedLine !== '') {
        // Just exited a table, add blank line after if next line isn't blank
        inTable = false;
        if (i + 1 < lines.length && lines[i + 1].trim() !== '') {
          result.push(line);
          result.push('');
          continue;
        }
      }
      inTable = false;
      result.push(line);
    }
  }
  
  return result;
}

/* ─────────────────────────────────────────────
   MAIN EXPORT
   ───────────────────────────────────────────── */

/**
 * Run all detectors and return a summary of what was found.
 * @param {string} markdown
 * @returns {{ issues: Array, counts: Object }}
 */
export function scanMarkdown(markdown) {
  if (!markdown || typeof markdown !== 'string') return { issues: [], counts: {} };
  const lines = markdown.split('\n');

  const allIssues = [
    ...detectDoubledHeadings(lines),
    ...detectDuplicateLines(lines),
    ...detectExcessiveBlankLines(lines),
    ...detectRepeatedH1s(lines),
    ...detectBoldAsHeading(lines),
    ...detectSkippedHeadingLevels(lines),
    ...detectRulerSpam(lines),
    ...detectMalformedTables(lines),
  ];

  const counts = allIssues.reduce((acc, issue) => {
    acc[issue.type] = (acc[issue.type] || 0) + 1;
    return acc;
  }, {});

  return { issues: allIssues, counts };
}

/**
 * Fix all detected issues and return cleaned markdown.
 * This is the main function to call before passing content to ReactMarkdown.
 * @param {string} markdown
 * @returns {string} cleaned markdown
 */
export function sanitizeMarkdown(markdown) {
  if (!markdown || typeof markdown !== 'string') return markdown || '';

  // Remove agent metadata header blockquote and trailing line divider
  let cleaned = markdown.replace(/(?:^|\n)>\s*\*\*(?:Agent|エージェント):\*\*.*?(?:\n\s*)*\n\s*---\s*(?:\n|$)/gi, '\n');
  cleaned = cleaned.replace(/(?:^|\n)>\s*\*\*(?:Agent|エージェント):\*\*.*?(?:\n|$)/gi, '\n');

  let lines = cleaned.split('\n');

  // Pass 0.5 – fix table cell content that can confuse markdown parser
  lines = fixTableCellContent(lines);

  // Pass 1 – fix individual line issues
  lines = lines.map(line => {
    let fixed = line;
    // Fix doubled heading markers
    if (/^(#{1,6}\s+)+#{1,6}\s/.test(fixed) || /^#{1,6}\s+#{1,6}/.test(fixed)) {
      fixed = fixDoubledHeadings(fixed);
    }
    return fixed;
  });

  // Pass 1.5 - Strip outer bold markers from standalone paragraphs or bullet/list items
  lines = lines.map(line => {
    const trimmed = line.trim();
    if (trimmed.startsWith('#')) return line;

    // e.g. "- **text**" or "1. **text**"
    const bulletMatch = line.match(/^(\s*[-*+]\s+|\s*\d+\.\s+)\*\*(.*?)\*\*\s*$/);
    if (bulletMatch) {
      return bulletMatch[1] + bulletMatch[2];
    }

    // e.g. "**text**"
    const paraMatch = line.match(/^(\s*)\*\*(.*?)\*\*\s*$/);
    if (paraMatch) {
      return paraMatch[1] + paraMatch[2];
    }

    return line;
  });

  // Pass 2 – trim heading whitespace
  lines = trimHeadingWhitespace(lines);

  // Pass 3 – fix skipped heading levels
  lines = fixSkippedHeadingLevels(lines);

  // Pass 4 – demote extra H1s
  lines = demoteExtraH1s(lines);

  // Pass 5 – convert standalone bold lines to headings
  lines = convertBoldLinesToHeadings(lines);

  // Pass 6 – remove consecutive duplicate lines
  lines = removeDuplicateLines(lines);

  // Pass 7 – remove ruler spam
  lines = removeRulerSpam(lines);

  // Pass 8 – collapse excessive blank lines (do last)
  lines = collapseExcessiveBlanks(lines);

  return lines.join('\n').trim();
}

/**
 * Returns a human-readable summary of what sanitizeMarkdown fixed.
 * Useful for debug logging.
 * @param {string} original
 * @param {string} sanitized
 * @returns {string}
 */
export function describeSanitizationDiff(original, sanitized) {
  if (original === sanitized) return 'No changes needed.';

  const origLines = original.split('\n').length;
  const sanLines = sanitized.split('\n').length;
  const diff = origLines - sanLines;

  const { counts } = scanMarkdown(original);
  const parts = [];

  if (counts.doubled_heading) parts.push(`${counts.doubled_heading} doubled heading(s)`);
  if (counts.duplicate_line) parts.push(`${counts.duplicate_line} duplicate line(s)`);
  if (counts.excess_blank) parts.push(`${counts.excess_blank} excess blank line(s)`);
  if (counts.repeated_h1) parts.push(`${counts.repeated_h1} repeated H1(s) → demoted to H2`);
  if (counts.bold_as_heading) parts.push(`${counts.bold_as_heading} bold line(s) → heading`);
  if (counts.skipped_heading) parts.push(`${counts.skipped_heading} skipped heading level(s)`);
  if (counts.ruler_spam) parts.push(`${counts.ruler_spam} redundant ruler(s)`);
  if (counts.table_list_confusion) parts.push(`${counts.table_list_confusion} table cell(s) fixed for parser compatibility`);

  return `Fixed: ${parts.join(', ')}. Lines: ${origLines} → ${sanLines} (−${diff})`;
}
